import { useEffect, useState } from "react";
import { formatCent } from "../../shared/money.js";
import type { AngebotListenEintrag, PositionDto } from "../../shared/types.js";
import { api } from "../api.js";
import { AngebotAnsicht } from "./AngebotAnsicht.js";
import { Kalkulation } from "./Kalkulation.js";

const STATUS_TEXT: Record<string, string> = {
  entwurf: "Entwurf",
  versendet: "Versendet",
  angenommen: "Angenommen",
  abgelehnt: "Abgelehnt",
  zurueckgezogen: "Zurückgezogen",
};

const datum = (iso: string | null) =>
  iso ? new Date(iso + "T00:00:00").toLocaleDateString("de-DE") : "—";

/**
 * Arbeitsfläche: Angebotsliste und Neuanlage nebeneinander.
 *
 * Die Liste steht links und schmal, weil daneben die Kalkulationsmaske
 * bereits zweispaltig ist (Formular plus mitlaufende Berechnung). Sie
 * bekommt deshalb nur so viel Platz, wie sie zum Nachschlagen braucht —
 * Nummer, Kunde, Summe, Status. Die Statusverwaltung liegt in der
 * Dokumentansicht, nicht in der schmalen Liste.
 */
export function Angebote() {
  const [liste, setListe] = useState<AngebotListenEintrag[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [suche, setSuche] = useState("");
  const [listeSichtbar, setListeSichtbar] = useState(true);
  const [uebernahme, setUebernahme] = useState<PositionDto[] | null>(null);

  function laden() {
    api
      .get<AngebotListenEintrag[]>("/angebote")
      .then(setListe)
      .catch((e) => setFehler(e.message));
  }
  useEffect(laden, []);

  // Positionen aus der Schnellkalkulation, im Ablagekorb hinterlegt.
  useEffect(() => {
    const korb = holeUebernahme();
    if (korb) setUebernahme(korb);
  }, []);

  if (offen)
    return <AngebotAnsicht id={offen} beiZurueck={() => { setOffen(null); laden(); }} />;

  const gefiltert = (liste ?? []).filter((a) =>
    [a.angebotsnr, a.kunde_firma]
      .join(" ")
      .toLowerCase()
      .includes(suche.toLowerCase()),
  );

  return (
    <div className={`arbeitsflaeche${listeSichtbar ? "" : " ohne-liste"}`}>
      <aside className="angebotsliste">
        <div className="pos-kopf">
          <span className="pos-nr">
            Angebote{liste && ` · ${liste.length}`}
          </span>
          <button
            className="knopf schlicht"
            onClick={() => setListeSichtbar(false)}
            title="Liste ausblenden"
          >
            ‹
          </button>
        </div>

        <input
          placeholder="Suchen …"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />

        {fehler && (
          <div className="meldung fehler" role="alert">
            {fehler}
          </div>
        )}
        {!liste && <p className="hinweistext">Wird geladen …</p>}
        {liste && !liste.length && (
          <p className="hinweistext">Noch keine Angebote. Rechts das erste anlegen.</p>
        )}

        <div className="angebotsliste-eintraege">
          {gefiltert.map((a) => (
            <button key={a.id} className="angebotskarte" onClick={() => setOffen(a.id)}>
              <div className="angebotskarte-kopf">
                <span className="angebotskarte-nr">{a.angebotsnr}</span>
                <span className={`abzeichen ${a.abgelaufen ? "abgelaufen" : a.status}`}>
                  {a.abgelaufen ? "Abgelaufen" : STATUS_TEXT[a.status]}
                </span>
              </div>
              <div className="angebotskarte-kunde">{a.kunde_firma}</div>
              <div className="angebotskarte-fuss">
                <span>{datum(a.angebotsdatum)}</span>
                <span className="zahl">{formatCent(a.summe_netto_cent)} €</span>
              </div>
            </button>
          ))}
          {liste?.length && !gefiltert.length ? (
            <p className="hinweistext">Kein Treffer für „{suche}“.</p>
          ) : null}
        </div>
      </aside>

      {!listeSichtbar && (
        <button
          className="knopf sekundaer listenschalter"
          onClick={() => setListeSichtbar(true)}
        >
          › Angebote
        </button>
      )}

      <div className="arbeitsflaeche-haupt">
        <Kalkulation
          uebernahme={uebernahme}
          beiGespeichert={() => {
            setUebernahme(null);
            laden();
          }}
        />
      </div>
    </div>
  );
}

/* ── Übergabekorb ─────────────────────────────────────────────────────── */

/**
 * Schnellkalkulation und Angebotsmaske hängen an verschiedenen Reitern und
 * werden beim Wechsel ausgehängt. Ohne Ablage dazwischen wäre die Übergabe
 * unmöglich. Wird beim Abholen sofort geleert — sonst tauchten dieselben
 * Positionen bei jedem späteren Besuch erneut auf.
 */
let korb: PositionDto[] | null = null;

export function legeUebernahmeAb(positionen: PositionDto[]): void {
  korb = positionen.length ? positionen : null;
}

function holeUebernahme(): PositionDto[] | null {
  const p = korb;
  korb = null;
  return p;
}
